import type { NextRouter } from "next/router";
import { fetchStockShortEsmaSummary, fetchStockShortInterest, fetchStockShortInterestPositions, fetchStockShortsDailyInterestNew, fetchStockShortsTimeRanges, shortsWarmKeys } from "@/controllers/stock-shorts";
import { warm } from "@/lib/warm-requests";
import { prefetchPageFiles, preloadNextData } from "@/lib/next-data-preload";
import { PreloadPlan } from "@/lib/tab-preload";
import { canonicalExchange, stockPath } from "@/lib/stock-url";
import { Stock } from "@/models/stock";

/**
 * Preloads the short-interest tab of a stock from its overview (ortex/ortex-gui#1350): the route's
 * code (fetched, not run), its `_next/data` props and, for signed-in readers, the API calls the tab makes on mount.
 * When to call it is decided by `useShortInterestPreload` (src/hooks/useShortInterestPreload.ts).
 */

export const SHORT_INTEREST_ROUTE = "/s/[exchange]/[ticker]/short-interest";

/** The cards' time range when the reader has not saved one (useStockShortsCards). */
export const DEFAULT_SHORTS_TIME_RANGE = "3m";
export const SHORTS_TIME_RANGE_SETTING = "StockShorts->/boxes/time-range";

type ShortsStock = Pick<Stock, "tradingitem_id" | "security_id">;

/** One card's series request, as useStockShortsCards sends it: [id, metric, timeRange, usStock, identifier]. */
export type CardSeriesArgs = [number, string, string, boolean, string];

/**
 * The series requests for the cards named by a top-boxes answer. Shared by the tab
 * (useStockShortsCards) and the preload, so both send exactly the same requests.
 */
export const cardSeriesArgs = (stock: ShortsStock, topBoxes: Record<string, unknown>, timeRange: string): CardSeriesArgs[] =>
    Object.keys(topBoxes ?? {})
        .filter((key) => key.startsWith("Box"))
        .map((boxKey) => topBoxes[boxKey] as { metric: string; identifier: string })
        .map(({ metric, identifier }) => [identifier === "security" ? stock.security_id : stock.tradingitem_id, metric, timeRange, !["flagged_ff", "holder"].includes(metric), identifier]);

/** Starts the requests the short-interest tab's overview makes on mount. Never rejects. */
export const warmShortInterestTab = async (stock: Pick<Stock, "tradingitem_id" | "security_id" | "shorts_type">, timeRange: string): Promise<void> => {
    const tid = stock.tradingitem_id;
    if (!tid) return;
    const quiet = <T>(p: Promise<T>) => p.catch(() => undefined);
    const jobs: Promise<unknown>[] = [quiet(warm(shortsWarmKeys.timeRanges(), fetchStockShortsTimeRanges))];
    if (stock.shorts_type === "FINRA") jobs.push(quiet(warm(shortsWarmKeys.finraPositions(tid), () => fetchStockShortInterestPositions(tid))));
    if (stock.shorts_type === "ESMA") jobs.push(quiet(warm(shortsWarmKeys.esmaSummary(tid), () => fetchStockShortEsmaSummary(tid))));
    jobs.push(
        quiet(
            warm(shortsWarmKeys.topBoxes(tid, timeRange), () => fetchStockShortInterest(tid, timeRange)).then((topBoxes) =>
                Promise.all(cardSeriesArgs(stock, topBoxes, timeRange).map((args) => quiet(warm(shortsWarmKeys.dailySeries(...args), () => fetchStockShortsDailyInterestNew(...args)))))
            )
        )
    );
    await Promise.all(jobs);
};

/** Runs the parts of `plan` for this stock's short-interest tab. Never rejects. */
export const preloadShortInterestTab = async (router: NextRouter, stock: Stock, plan: PreloadPlan, timeRange: string): Promise<{ code: boolean; data: boolean; api: boolean }> => {
    const done = { code: false, data: false, api: false };
    if (!stock?.ticker || !stock.exchangesymbol) return done;
    const asPath = stockPath(stock, "short-interest");
    try {
        // The code is fetched, not run (see prefetchPageFiles): the run waits for the click.
        if (plan.code) done.code = prefetchPageFiles(SHORT_INTEREST_ROUTE);
        if (plan.data) done.data = await preloadNextData(router, SHORT_INTEREST_ROUTE, { exchange: canonicalExchange(stock.exchangesymbol), ticker: stock.ticker }, asPath);
        if (plan.api) {
            await warmShortInterestTab(stock, timeRange);
            done.api = true;
        }
    } catch {
        /* a preload is only ever a head start */
    }
    return done;
};
